// MOMERY CUSTOM — Printess editor state integration.
(function () {
  if (window.__printessEditorStateInit) return;
  window.__printessEditorStateInit = true;

  const POLL_MS = 600;
  const BADGE_ID = 'PrintessEditorStateBadge';
  const STYLE_ID = 'PrintessEditorStateStyle';
  const REGISTER_RETRY_INTERVAL_MS = 250;
  const REGISTER_TIMEOUT_MS = 10000;

  let currentApi = null;
  let currentComponent = null;
  let pollingInterval = null;
  let lastDirty = null;
  let hasSavedOnce = false;
  let surchargeObserver = null;
  let surchargeRoot = null;

  const getShadowRoot = () => currentComponent?.shadowRoot || null;

  function ensureStyle(root) {
    if (!root || root.getElementById?.(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      [data-printess-page-surcharge] {
        font-weight: 700;
      }

      #${BADGE_ID} {
        margin-left: 3px;
        font-size: 12px;
        font-weight: 600;
        padding: 6px 10px;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        white-space: nowrap;
        line-height: 1;
        user-select: none;
      }

      #${BADGE_ID} + bc-button[action="show-photobook-wizzard"] {
        margin-left: 19px;
      }

      #${BADGE_ID}[data-state="saved"] {
        background: rgba(16, 185, 129, 0.12);
        color: rgba(16, 185, 129, 1);
      }

      #${BADGE_ID}[data-state="unsaved"] {
        background: rgba(245, 158, 11, 0.14);
        color: rgba(245, 158, 11, 1);
      }
    `;

    root.appendChild(style);
  }

  function emphasizePageSurcharge(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const matches = [];
    let node;

    while ((node = walker.nextNode())) {
      if (node.parentElement?.closest('style, script, [data-printess-page-surcharge]')) continue;

      const match = /\+€[\d.]+,\d{2} EUR Seitenaufpreis/.exec(node.textContent);
      if (match) matches.push({ node, match });
    }

    matches.forEach(({ node, match }) => {
      const surchargeNode = node.splitText(match.index);
      surchargeNode.splitText(match[0].length);

      const span = document.createElement('span');
      span.setAttribute('data-printess-page-surcharge', '');
      surchargeNode.replaceWith(span);
      span.appendChild(surchargeNode);
    });
  }

  function disconnectSurchargeObserver() {
    surchargeObserver?.disconnect();
    surchargeObserver = null;
    surchargeRoot = null;
  }

  function observePageSurcharge(root) {
    if (surchargeRoot === root) return;
    disconnectSurchargeObserver();
    surchargeRoot = root;

    const options = { childList: true, characterData: true, subtree: true };
    const sync = () => {
      // Do not observe our own wrapping mutations or enqueue another sync.
      surchargeObserver.disconnect();
      try {
        ensureStyle(root);
        emphasizePageSurcharge(root);
      } finally {
        surchargeObserver.observe(root, options);
      }
    };

    surchargeObserver = new MutationObserver((records) => {
      const hasEditorChanges = records.some(({ target }) => {
        const element = target.nodeType === Node.ELEMENT_NODE ? target : target.parentElement;
        return !element?.closest(`#${BADGE_ID}, #${STYLE_ID}`);
      });
      if (hasEditorChanges) sync();
    });
    sync();
  }

  function findSaveAnchor(root) {
    if (!root) return null;

    const selector = [
      'bc-button[action="shopSave"]',
      'bc-button[action="save"]',
      'bc-button[action="save+close"]',
      'bc-button[action="save+basket"]',
    ].join(',');

    const saveButton = root.querySelector(selector);
    if (saveButton) return saveButton;

    return Array.from(root.querySelectorAll('button, bc-button')).find((button) => {
      const text = (button.textContent || '').trim().toUpperCase();
      const label = (button.getAttribute('label') || '').trim().toUpperCase();

      return text === 'SPEICHERN' || label === 'SPEICHERN';
    }) || null;
  }

  function ensureBadge(root) {
    if (!root) return null;

    const existingBadge = root.getElementById?.(BADGE_ID) || root.querySelector(`#${BADGE_ID}`);
    if (existingBadge) return existingBadge;

    const anchor = findSaveAnchor(root);
    if (!anchor) return null;

    const badge = document.createElement('span');
    badge.id = BADGE_ID;
    badge.setAttribute('data-state', 'unsaved');
    badge.textContent = 'Nicht gespeichert';

    anchor.insertAdjacentElement('afterend', badge);

    return badge;
  }

  function readDirty() {
    if (!currentApi || typeof currentApi.hasUnsavedChanges !== 'function') {
      return lastDirty ?? true;
    }

    try {
      return !!currentApi.hasUnsavedChanges();
    } catch (err) {
      console.warn('[PRINTESS][STATE] hasUnsavedChanges() failed:', err);
      return lastDirty ?? true;
    }
  }

  function setBadgeState(root, dirty, savedOnce) {
    const badge = ensureBadge(root);
    if (!badge) return;

    const isSaved = !!savedOnce && !dirty;
    const state = isSaved ? 'saved' : 'unsaved';

    badge.setAttribute('data-state', state);
    badge.textContent = isSaved ? 'Gespeichert' : 'Nicht gespeichert';
  }

  function syncBadge() {
    const root = getShadowRoot();
    if (!root) return;

    observePageSurcharge(root);

    const dirty = readDirty();
    lastDirty = dirty;

    setBadgeState(root, dirty, hasSavedOnce);
  }

  function scheduleBadgeSync() {
    window.setTimeout(syncBadge, 0);
    window.setTimeout(syncBadge, 300);
    window.setTimeout(syncBadge, 1000);
  }

  function startPolling() {
    if (pollingInterval) window.clearInterval(pollingInterval);

    pollingInterval = window.setInterval(syncBadge, POLL_MS);
  }

  function initEditorState(api, component) {
    currentApi = api;
    currentComponent = component;
    hasSavedOnce = false;
    lastDirty = null;

    const root = getShadowRoot();
    if (root) observePageSurcharge(root);

    startPolling();
    scheduleBadgeSync();
  }

  document.addEventListener('printess:saved', () => {
    hasSavedOnce = true;
    scheduleBadgeSync();
  });

  const registerPlugin = () => {
    if (window.__printessEditorStatePluginRegistered) return true;
    if (typeof window.registerPrintessPlugin !== 'function') return false;

    window.registerPrintessPlugin({
      onOpenAsync(context) {
        const api = context?.integration?.getApi?.() || null;
        const component = context?.integration?.getComponent?.() || null;

        if (!api || !component) {
          console.warn('[PRINTESS][STATE] Missing api or component in onOpenAsync', {
            hasApi: !!api,
            hasComponent: !!component,
          });

          return Promise.resolve();
        }

        initEditorState(api, component);

        return Promise.resolve();
      },
      onCloseAsync() {
        disconnectSurchargeObserver();
        currentComponent = null;
        return Promise.resolve();
      },
    });

    window.__printessEditorStatePluginRegistered = true;
    return true;
  };

  const waitForPluginRegistration = () => {
    if (registerPlugin()) return;

    const startedAt = Date.now();
    const retryTimer = window.setInterval(() => {
      if (registerPlugin()) {
        window.clearInterval(retryTimer);
        return;
      }

      if (Date.now() - startedAt >= REGISTER_TIMEOUT_MS) {
        window.clearInterval(retryTimer);
        console.warn('[PRINTESS][STATE] registerPrintessPlugin is not available');
      }
    }, REGISTER_RETRY_INTERVAL_MS);
  };

  waitForPluginRegistration();

  window.addEventListener('pagehide', () => {
    disconnectSurchargeObserver();
    if (pollingInterval) window.clearInterval(pollingInterval);
  });
})();
